import { getIsMobile } from "../../helpers/getIsMobile.js";
import { TIPS_CONFIG, resolveResponsive } from "./config.js";
import { CSS_CLASSES } from "./constants.js";
import { tipsStorage } from "./storage.js";
import { renderTipsList } from "./templates.js";
import { UI_OFFSETS } from "./ui.config.js";

export class TipsService {
    #overlay;
    #state = { isVisible: false, currentPage: null };
    #events = new Map();
    #targetsCache = new Map();
    #resizeObservers = new Map();

    constructor(overlay) {
        if (!(overlay instanceof HTMLElement)) {
            throw new Error("Overlay element must be an HTMLElement");
        }
        this.#overlay = overlay;
        this.#bindEvents();
    }

    // -------------------- Публичные методы --------------------
    show(pageName) {
        const allTips = TIPS_CONFIG[pageName];
        if (!allTips || !this.#overlay) return;

        const activeTips = allTips.filter((tip) => {
            const isMobile = getIsMobile();
            const display = tip.display || "all";

            if (display === "all") return true;
            if (display === "mobile") return isMobile;
            if (display === "desktop") return !isMobile;

            return true;
        });

        const tipsWithId = activeTips.map((tip, index) => ({
            ...tip,
            id: `tip-${pageName}-${index}`,
        }));

        this.#overlay.innerHTML = renderTipsList(tipsWithId);
        this.#state = { ...this.#state, isVisible: true, currentPage: pageName };

        requestAnimationFrame(() => {
            this.#overlay.classList.add(CSS_CLASSES.SHOW);

            tipsWithId.forEach((tip) => {
                const tipEl = document.getElementById(tip.id);
                if (!tipEl || !tip.target) return;

                this.#positionTip(tipEl, tip.target, tip.orientation);
                this.#observeTarget(tipEl, tip.target, tip.orientation);
            });

            this.#emit("show", pageName);
        });
    }

    hide() {
        if (!this.#overlay) return;

        this.#overlay.classList.remove(CSS_CLASSES.SHOW);
        this.#state.isVisible = false;

        this.#targetsCache.clear();
        this.#disconnectObservers();

        const onTransitionEnd = (e) => {
            if (e.target === this.#overlay && e.propertyName === "opacity") {
                this.#overlay.innerHTML = "";
                this.#overlay.removeEventListener("transitionend", onTransitionEnd);
            }
        };

        this.#overlay.addEventListener("transitionend", onTransitionEnd);
        this.#emit("hide", this.#state.currentPage);
    }

    showOnce(pageName) {
        if (!tipsStorage.isPageShown(pageName)) {
            this.show(pageName);
            tipsStorage.markAsShown(pageName);
        }
    }

    on(event, callback) {
        if (!this.#events.has(event)) this.#events.set(event, []);
        this.#events.get(event).push(callback);
    }

    getState() {
        return { ...this.#state };
    }

    destroy() {
        this.hide();
        this.#overlay.removeEventListener("click", this.#handleOverlayClick);
        window.removeEventListener("scroll", this.#handleScroll);
    }

    // -------------------- Приватные методы --------------------
    #bindEvents() {
        this.#overlay.addEventListener("click", this.#handleOverlayClick);
        window.addEventListener("scroll", this.#handleScroll);
    }

    #handleOverlayClick = () => this.hide();

    #handleScroll = () => {
        if (!this.#state.isVisible) return;

        const tipElements = this.#overlay.querySelectorAll(`.${CSS_CLASSES.TIP}`);
        if (!tipElements?.length) return;

        tipElements.forEach((tip, index) => {
            const tipId = `tip-${this.#state.currentPage}-${index}`;
            const tipEl = document.getElementById(tipId);

            if (tipEl) this.#positionTip(tipEl, tip.target, tip.orientation);
        });
    };

    #positionTip(tipEl, targetSelector, rawOrientation = "top") {
        const targetEl = this.#getTargetElement(targetSelector);
        if (!targetEl) return;

        const orientation = resolveResponsive(rawOrientation);

        tipEl.setAttribute("data-tip-orientation", orientation);

        const rect = targetEl.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

        const offset = UI_OFFSETS.TIP_OFFSET;
        const tipWidth = tipEl.offsetWidth;
        const tipHeight = tipEl.offsetHeight;

        let top = 0,
            left = 0;

        switch (orientation) {
            case "top":
                top = rect.top + scrollTop - tipHeight - offset;
                left = rect.left + scrollLeft + rect.width / 2 - tipWidth / 2;
                break;
            case "bottom":
                top = rect.bottom + scrollTop + offset;
                left = rect.left + scrollLeft + rect.width / 2 - tipWidth / 2;
                break;
            case "left":
                top = rect.top + scrollTop + rect.height / 2 - tipHeight / 2;
                left = rect.left + scrollLeft - tipWidth - offset;
                break;
            case "right":
                top = rect.top + scrollTop + rect.height / 2 - tipHeight / 2;
                left = rect.right + scrollLeft + offset;
                break;
        }

        ({ top, left } = this.#clampToViewport({ top, left, tipWidth, tipHeight }));

        tipEl.style.top = `${top}px`;
        tipEl.style.left = `${left}px`;
    }

    #getTargetElement(selector) {
        const cached = this.#targetsCache.get(selector);
        if (cached && document.contains(cached)) return cached;

        const el = document.querySelector(selector);
        if (el) this.#targetsCache.set(selector, el);
        return el;
    }

    #clampToViewport({ top, left, tipWidth, tipHeight }) {
        const padding = UI_OFFSETS.VIEWPORT_PADDING;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        if (left < padding) left = padding;
        if (left + tipWidth > vw - padding) left = vw - tipWidth - padding;
        if (top < padding) top = padding;
        if (top + tipHeight > vh - padding) top = vh - tipHeight - padding;

        return { top, left };
    }

    #emit(event, payload) {
        const listeners = this.#events.get(event) || [];
        listeners.forEach((cb) => cb(payload));
    }

    // -------------------- ResizeObserver --------------------
    #observeTarget(tipEl, targetSelector, orientation) {
        const targetEl = this.#getTargetElement(targetSelector);
        if (!targetEl) return;

        const observer = new ResizeObserver(() => {
            this.#positionTip(tipEl, targetSelector, orientation);
        });

        observer.observe(targetEl);
        this.#resizeObservers.set(tipEl.id, observer);
    }

    #disconnectObservers() {
        this.#resizeObservers.forEach((observer) => observer.disconnect());
        this.#resizeObservers.clear();
    }
}
